<template>
  <div class="keyword-input-container">
    <!-- Input field for typing keywords -->
    <div class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus-within:ring-2 focus-within:ring-primary focus-within:border-primary transition duration-200 min-h-[80px]">
      <!-- Display existing keywords as tags -->
      <div class="flex flex-wrap gap-2 mb-2" v-if="keywords.length > 0">
        <span
          v-for="(keyword, index) in keywords"
          :key="index"
          class="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-sm rounded-md"
        >
          {{ keyword }}
          <button
            @click="removeKeyword(index)"
            class="ml-1 text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100 focus:outline-none"
            type="button"
          >
            <X class="w-3 h-3" />
          </button>
        </span>
      </div>
      
      <!-- Input field -->
      <input
        ref="inputRef"
        v-model="currentInput"
        @keydown="handleKeydown"
        @keyup="handleKeyup"
        type="text"
        class="w-full bg-transparent border-none outline-none focus:ring-0 p-0"
        :placeholder="keywords.length === 0 ? placeholder : 'Add more keywords...'"
      />
    </div>
    
    <!-- Helper text -->
    <p class="text-xs text-secondary-text dark:text-secondary-dark-text mt-1">
      Type keywords and press space or enter to add them as tags
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { X } from 'lucide-vue-next'

interface Props {
  modelValue: string[]
  placeholder?: string
}

interface Emits {
  (e: 'update:modelValue', value: string[]): void
}

// Props and emits
const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Enter keywords or key points...'
})

const emit = defineEmits<Emits>()

// Local state
const currentInput = ref('')
const inputRef = ref<HTMLInputElement>()

// Use a computed property to get keywords directly from props
const keywords = computed({
  get: () => props.modelValue || [],
  set: (value: string[]) => emit('update:modelValue', value)
})

// Handle keyboard input
const handleKeydown = (event: KeyboardEvent) => {
  // Handle space, enter, comma, or semicolon as delimiters
  if (event.key === ' ' || event.key === 'Enter' || event.key === ',' || event.key === ';') {
    event.preventDefault()
    addKeyword()
  }
  
  // Handle backspace when input is empty to remove last keyword
  if (event.key === 'Backspace' && currentInput.value === '' && keywords.value.length > 0) {
    event.preventDefault()
    removeKeyword(keywords.value.length - 1)
  }
}

const handleKeyup = (event: KeyboardEvent) => {
  // Also handle space on keyup to ensure it's captured
  if (event.key === ' ') {
    addKeyword()
  }
}

// Add a new keyword
const addKeyword = () => {
  const trimmedInput = currentInput.value.trim()
  
  if (trimmedInput === '') {
    return
  }
  
  // Split by common delimiters and add each part as a keyword
  const newKeywords = trimmedInput
    .split(/[,;]/)
    .map(keyword => keyword.trim())
    .filter(keyword => keyword !== '' && !keywords.value.includes(keyword))
  
  if (newKeywords.length > 0) {
    keywords.value = [...keywords.value, ...newKeywords]
    currentInput.value = ''
  }
}

// Remove a keyword by index
const removeKeyword = (index: number) => {
  if (index >= 0 && index < keywords.value.length) {
    const newKeywords = [...keywords.value]
    newKeywords.splice(index, 1)
    keywords.value = newKeywords
  }
}

// Focus the input when the component is clicked
const focusInput = () => {
  inputRef.value?.focus()
}

// Expose methods for parent components
defineExpose({
  focus: focusInput,
  addKeyword,
  removeKeyword
})
</script>

<style scoped>
.keyword-input-container {
  width: 100%;
}

/* Remove default input styles */
input:focus {
  outline: none;
  box-shadow: none;
}
</style>